import L from "leaflet";
import "leaflet-draw";
import { useEffect, useRef } from "react";
import { usePathology } from "./PathologyRoot";
import PathologyMapLayers from "./PathologyMapLayers";

/**
 * Map-only pathology effects: overlays, pins, and admin ROI drawing.
 * Keeps the slide map clean — no floating panels.
 */
export default function PathologyMapEffects() {
  const { isAdmin, admin, student, mapRef, mapReadyVersion } = usePathology();

  const roiLayerRef = useRef(null);
  const pathologyDrawControlRef = useRef(null);

  const roiDrawActive = isAdmin ? admin.roiDrawActive : false;
  const setRoiBounds = isAdmin ? admin.setRoiBounds : null;

  useEffect(() => {
    if (!isAdmin) return undefined;
    const map = mapRef?.current;
    if (!map || !roiDrawActive) return undefined;

    const fg = new L.FeatureGroup();
    map.addLayer(fg);

    const drawControl = new L.Control.Draw({
      position: "topleft",
      draw: {
        marker: false,
        polygon: false,
        polyline: false,
        circle: false,
        circlemarker: false,
        rectangle: {
          shapeOptions: {
            color: "#d32f2f",
            weight: 2,
            fillOpacity: 0.2,
          },
        },
      },
      edit: false,
    });
    map.addControl(drawControl);
    pathologyDrawControlRef.current = drawControl;

    const onCreated = (e) => {
      if (e.layerType !== "rectangle") return;
      if (roiLayerRef.current) {
        fg.removeLayer(roiLayerRef.current);
      }
      const layer = e.layer;
      fg.addLayer(layer);
      roiLayerRef.current = layer;
      setRoiBounds?.(layer.getBounds());
    };

    map.on(L.Draw.Event.CREATED, onCreated);

    return () => {
      map.off(L.Draw.Event.CREATED, onCreated);
      if (pathologyDrawControlRef.current) {
        map.removeControl(pathologyDrawControlRef.current);
        pathologyDrawControlRef.current = null;
      }
      if (roiLayerRef.current) {
        try {
          fg.removeLayer(roiLayerRef.current);
        } catch (_) {
          /* ignore */
        }
        roiLayerRef.current = null;
      }
      map.removeLayer(fg);
    };
  }, [isAdmin, mapRef, mapReadyVersion, roiDrawActive, setRoiBounds]);

  if (isAdmin) {
    return (
      <PathologyMapLayers
        mapRef={mapRef}
        mapReadyVersion={mapReadyVersion}
        overlays={admin.acceptedOverlays}
        previewOverlay={admin.previewOverlay}
        previewOpacity={admin.previewOpacity}
        showPins
      />
    );
  }

  return (
    <PathologyMapLayers
      mapRef={mapRef}
      mapReadyVersion={mapReadyVersion}
      overlays={student.selectedDiseaseId ? student.acceptedOverlays : []}
      showPins
    />
  );
}
